import React from "react";
import { Slider, Box, Typography } from "@mui/material";
import type { SliderProps } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";

type TextSizeControlProps = {
  textSize: number; // Value in rem units
  onTextSizeChange: (size: number) => void;
  label?: string;
  ariaLabel?: string;
  minLabel?: string;
  maxLabel?: string;
  min?: number;
  max?: number;
  step?: number;
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "1.2rem",
    padding: "0.4rem 0",
    border: "none",
    margin: 0,
    minWidth: 0,
  },
  label: {
    fontSize: "1.2rem",
    color: "#666",
    whiteSpace: "nowrap",
    marginRight: "0.8rem",
    fontWeight: 600,
  },
  sliderWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "1.2rem",
    minWidth: "20rem",
  },
  minMaxLabel: {
    fontSize: "1.0rem",
    color: "#666",
    fontWeight: 500,
  },
  slider: {
    color: "#0078d4", // Fluent UI blue - consistent with design system
    flex: 1,
    margin: "0 0.4rem", // Add horizontal margin to prevent thumb overlap
    "& .MuiSlider-thumb": {
      width: 16,
      height: 16,
      backgroundColor: "#fff",
      border: "0.2rem solid currentColor",
      "&:hover": {
        boxShadow: "0 0 0 0.8rem rgba(0, 120, 212, 0.16)",
      },
    },
    "& .MuiSlider-track": {
      height: 4,
    },
    "& .MuiSlider-rail": {
      height: 4,
      opacity: 0.3,
    },
  },
}));

function TextSizeControl({
  textSize,
  onTextSizeChange,
  label = "",
  ariaLabel = "",
  minLabel = "",
  maxLabel = "",
  min = 1.0, // 1.0rem = 10px baseline
  max = 1.75, // 1.75rem = 17.5px (large size)
  step = 0.25, // Step in rem units
}: TextSizeControlProps) {
  const classes = useStyles();

  const handleChange: NonNullable<SliderProps["onChange"]> = (
    _event,
    newValue,
  ) => {
    if (typeof newValue === "number") {
      onTextSizeChange(newValue);
    }
  };

  return (
    <fieldset
      className={String(classes.root)}
      aria-label={ariaLabel}
      data-testid="text-size-control"
    >
      {label && (
        <Typography
          component="label"
          className={String(classes.label)}
          htmlFor="text-size-slider"
        >
          {label}
        </Typography>
      )}

      <Box className={String(classes.sliderWrapper)}>
        {minLabel && (
          <Typography
            component="span"
            className={String(classes.minMaxLabel)}
            aria-hidden
          >
            {minLabel}
          </Typography>
        )}
        <Slider
          id="text-size-slider"
          value={textSize}
          min={min}
          max={max}
          step={step}
          onChange={handleChange}
          aria-label={ariaLabel}
          size="small"
          className={String(classes.slider)}
          valueLabelDisplay="off"
        />
        {maxLabel && (
          <Typography
            component="span"
            className={String(classes.minMaxLabel)}
            aria-hidden
          >
            {maxLabel}
          </Typography>
        )}
      </Box>
    </fieldset>
  );
}

export default TextSizeControl;
